import { rankOf, suitOf } from './cards';
import type { TiePolicy } from './rules';
import { isTrumpEligible } from './trump';
import type { Play, TrumpState } from './types';

/**
 * PRD §4.3: highest eligible trump wins, otherwise the highest card of the led suit. With two packs,
 * equal suit+rank ties resolve by play order (LAST_PLAYED by default). Pack index never changes rank.
 */
export function resolveTrick(
  plays: readonly Play[],
  trickIndex: number,
  trump: TrumpState,
  tie: TiePolicy,
): Play {
  if (plays.length === 0) throw new Error('Cannot resolve an empty trick');
  const ordered = [...plays].sort((a, b) => a.order - b.order);
  const ledSuit = suitOf(ordered[0]!.cardId);
  const trumps = ordered.filter((p) => isTrumpEligible(trump, trickIndex, p));
  const pool = trumps.length > 0 ? trumps : ordered.filter((p) => suitOf(p.cardId) === ledSuit);
  let best = pool[0]!;
  for (const play of pool.slice(1)) {
    const diff = rankOf(play.cardId) - rankOf(best.cardId);
    if (diff > 0 || (diff === 0 && tie === 'LAST_PLAYED')) best = play;
  }
  return best;
}

/** The play currently winning an incomplete trick (used by bots and the UI's "winning" highlight). */
export function currentWinner(
  plays: readonly Play[],
  trickIndex: number,
  trump: TrumpState,
  tie: TiePolicy,
): Play | null {
  return plays.length === 0 ? null : resolveTrick(plays, trickIndex, trump, tie);
}
